import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { IMAGES, ZEFFY_DONATE_URL, ZEFFY_5K_URL } from "@/lib/api";
import { Heart, Star, Sparkles, Users, HandHeart, Footprints, PencilLine, Gift } from "lucide-react";

const ActionCard = ({ to, href, title, desc, icon: Icon, testid, accentClass }) => {
    const inner = (
        <>
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accentClass}`}>
                <Icon size={22} className="text-white" />
            </div>
            <div className="mt-5 font-serif text-2xl text-stone-900">{title}</div>
            <p className="mt-2 text-sm text-stone-600 leading-relaxed">{desc}</p>
            <div className="mt-5 text-xs tracking-[0.3em] uppercase text-brand-purple group-hover:translate-x-1 transition-transform">Explore →</div>
        </>
    );
    if (href) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
               className="group block bg-brand-cream rounded-3xl p-6 border border-stone-100 hover-lift">
                {inner}
            </a>
        );
    }
    return (
        <Link to={to} data-testid={testid} className="group block bg-brand-cream rounded-3xl p-6 border border-stone-100 hover-lift">
            {inner}
        </Link>
    );
};

export default function Home() {
    return (
        <PageLayout>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7" data-testid="home-hero">
                        <div className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-brand-purple bg-purple-50 border border-purple-100 rounded-full px-4 py-2">
                            <Sparkles size={14} /> Real Support · Real Impact
                        </div>
                        <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-stone-900">
                            Family is <span className="text-brand-red">Forever.</span><br />
                            <span className="font-accent text-brand-purple text-6xl sm:text-7xl">Join the village.</span>
                        </h1>
                        <p className="mt-6 text-lg text-stone-600 max-w-xl leading-relaxed">
                            Poppa &amp; Gigi&apos;s Promise uplifts grandparents and kinship caregivers raising children the second time around — through community, resources, and a whole lot of love.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href={ZEFFY_DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-donate" className="px-7 py-3 rounded-full bg-brand-purple text-white font-semibold tracking-wide shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition">Support a Family</a>
                            <Link to="/register" data-testid="hero-cta-register" className="px-7 py-3 rounded-full border-2 border-stone-900 text-stone-900 font-semibold tracking-wide hover:bg-stone-900 hover:text-white transition">Get Support</Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-amber-200/60 blur-2xl" />
                        <div className="absolute -bottom-8 -right-6 w-28 h-28 rounded-full bg-purple-200/60 blur-2xl" />
                        <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-brand-cream bg-brand-cream">
                            <img
                                src={IMAGES.welcome}
                                alt="Family welcome"
                                className="w-full h-auto block"
                            />
                        </div>
                        <div className="absolute -bottom-6 -left-6 bg-brand-cream rounded-2xl shadow-xl px-5 py-4 border border-stone-100 flex items-center gap-3">
                            <Heart className="text-brand-red" />
                            <div>
                                <div className="text-xs uppercase tracking-widest text-stone-500">Be part of</div>
                                <div className="font-serif text-xl">The Village</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MISSION */}
            <section className="relative doodle-bg overflow-hidden" data-testid="home-mission">
                <Star className="absolute top-12 left-8 text-rose-300/70" size={28} />
                <Star className="absolute top-20 right-16 text-sky-300/70" size={20} />
                <Heart className="absolute bottom-16 left-20 text-purple-300/70" size={24} />
                <Heart className="absolute bottom-24 right-12 text-lime-400/70" size={26} />
                <img
                    src={IMAGES.illustLeft}
                    alt=""
                    className="hidden lg:block absolute left-4 bottom-10 w-44 xl:w-52 rounded-xl opacity-95 drop-shadow-xl rotate-[-3deg]"
                    aria-hidden="true"
                />
                <img
                    src={IMAGES.illustRight}
                    alt=""
                    className="hidden lg:block absolute right-4 bottom-10 w-52 xl:w-60 opacity-95 drop-shadow-xl rotate-[3deg]"
                    aria-hidden="true"
                />
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
                    <div className="text-xs tracking-[0.4em] uppercase text-brand-blue">Our Mission</div>
                    <h2 className="mt-4 font-serif text-5xl sm:text-7xl tracking-tight text-stone-900">
                        OUR <span className="text-brand-blue">MISSION</span>
                    </h2>
                    <p className="mt-8 text-lg md:text-xl text-stone-700 leading-relaxed max-w-3xl mx-auto">
                        Support those who step up when it matters most. We provide resources, awareness, and community for grandparents raising grandchildren. By creating safe spaces, hosting events, and connecting families to support systems, we lighten the load and strengthen every household we touch.
                    </p>
                    <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                        {[
                            { icon: Users, label: "Community" },
                            { icon: HandHeart, label: "Resources" },
                            { icon: Sparkles, label: "Hope" },
                        ].map((b, i) => (
                            <div key={i} className="bg-brand-cream/70 backdrop-blur rounded-2xl p-5 border border-white">
                                <b.icon className="mx-auto text-brand-purple" />
                                <div className="mt-2 text-sm uppercase tracking-[0.25em]">{b.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STORIES + ABOUT */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-8">
                <div className="rounded-3xl overflow-hidden bg-brand-cream border border-stone-200 hover-lift">
                    <img
                        src={IMAGES.hawkins}
                        alt="The Hawkins Family"
                        className="w-full h-auto block"
                    />
                    <div className="p-7">
                        <div className="text-xs tracking-[0.3em] uppercase text-brand-red">Featured Story</div>
                        <h3 className="font-serif text-3xl mt-2">The Hawkins Family</h3>
                        <p className="mt-3 text-stone-600 leading-relaxed">
                            A grandparent-led household finding strength in community — celebrating love, healing, and second chances every day.
                        </p>
                    </div>
                </div>
                <div className="rounded-3xl overflow-hidden bg-brand-cream border border-stone-200 hover-lift">
                    <img
                        src={IMAGES.founders}
                        alt="Founders"
                        className="w-full h-auto block"
                    />
                    <div className="p-7">
                        <div className="text-xs tracking-[0.3em] uppercase text-brand-purple">About Us</div>
                        <h3 className="font-serif text-3xl mt-2">From Our Journey, To Yours</h3>
                        <p className="mt-3 text-stone-600 leading-relaxed">
                            Founded with a deep understanding of the challenges and sacrifices that come with raising children a second time around. What started as a personal journey has grown into a mission to support every family walking the same path.
                        </p>
                    </div>
                </div>
            </section>

            {/* ACTION GRID */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
                <div className="text-center mb-10">
                    <div className="text-xs tracking-[0.4em] uppercase text-brand-lime">Take a Step</div>
                    <h2 className="font-serif text-4xl sm:text-5xl mt-3">Ways to walk with us</h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ActionCard to="/register" title="Register Now" desc="Caregivers — connect with resources, events, and the community you deserve." icon={Users} accentClass="bg-brand-purple" testid="action-register" />
                    <ActionCard to="/sponsor" title="Become a Sponsor" desc="Businesses & individuals who want to fuel real change — start here." icon={Gift} accentClass="bg-brand-blue" testid="action-sponsor" />
                    <ActionCard to="/volunteer" title="Volunteer" desc="Lend your time, skills, and heart to families in our village." icon={HandHeart} accentClass="bg-brand-lime" testid="action-volunteer" />
                    <ActionCard to="/send-note" title="Send a Note" desc="Write an encouraging message to a grandparent or caregiver today." icon={PencilLine} accentClass="bg-brand-red" testid="action-send-note" />
                    <ActionCard href={ZEFFY_DONATE_URL} title="Donate" desc="Every dollar helps a household survive — and thrive." icon={Heart} accentClass="bg-brand-purple-light" testid="action-donate" />
                    <ActionCard href={ZEFFY_5K_URL} title="5K Run Info" desc="Lace up for the cause — sign up for our Step Forward Community Walk." icon={Footprints} accentClass="bg-stone-900" testid="action-5k" />
                </div>
            </section>

            {/* GET INVOLVED BANNER */}
            <section className="mt-12">
                <div className="bg-brand-navy text-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
                        <h2 className="font-serif text-5xl sm:text-6xl tracking-tight">GET INVOLVED TODAY!</h2>
                        <p className="mt-4 text-stone-300 max-w-2xl mx-auto">Three quick ways to make an impact right now.</p>
                        <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                            <Link to="/send-note" data-testid="involved-send-note" className="underline-cta text-white text-sm py-4 hover:text-brand-lime-light transition">SEND A NOTE</Link>
                            <a href={ZEFFY_DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="involved-donate" className="underline-cta text-white text-sm py-4 hover:text-brand-lime-light transition">DONATE</a>
                            <a href={ZEFFY_5K_URL} target="_blank" rel="noopener noreferrer" data-testid="involved-5k" className="underline-cta text-white text-sm py-4 hover:text-brand-lime-light transition">5K RUN INFO</a>
                        </div>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
